import { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { cn } from '@/core/lib/utils';
import type { ProjectField, FieldValue } from '@/features/custom-fields/public';
import type { DateFormat, TimeFormat } from '@/core/stores/ui.store';
import type { PlannedDate } from '@/shared/types/datetime';
import type { Task, TaskColumnId, TaskPriority } from '../../types';
import { useTaskSubtasks } from '../../hooks/useTaskSubtasks';
import { OverflowMenu } from '../OverflowMenu';
import { InlineTaskRow } from './InlineTaskRow';
import {
  BUILT_IN_COLUMN_MAP,
  makeCustomFieldColumn,
  getColumnWidthPx,
  type RowContext,
} from './columnRegistry';
import { TaskTitleCell } from './TaskTitleCell';

interface TaskRowProps {
  task: Task;
  visibleColumns: TaskColumnId[];
  columnWidths: Record<string, number>;
  boardId?: string;
  timeFormat: TimeFormat;
  dateFormat?: DateFormat;
  fieldMap: Record<string, ProjectField>;
  /** Batch-fetched custom field values for the visible rows, keyed by task id. */
  customFieldValuesByTask?: Record<string, FieldValue[]>;
  isSelected?: boolean;
  isSubtaskSelected?: (id: string) => boolean;
  depth?: number;
  onSelect?: (id: string, selected: boolean) => void;
  onOpenDetail?: (id: string) => void;
  onTitleSave?: (id: string, title: string) => void;
  onStatusChange?: (id: string, statusId: string) => void;
  onPriorityChange?: (id: string, priority: TaskPriority | null) => void;
  onAssigneeChange?: (id: string, assigneeId: string | null) => void;
  onDateChange?: (id: string, start: PlannedDate | null, end: PlannedDate | null) => void;
  onEstimatedChange?: (id: string, minutes: number | null) => void;
  onGroupChange?: (id: string, groupId: string | null) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSubtaskCreate?: (parentId: string, title: string) => void;
  hideDragHandle?: boolean;
  hideCheckbox?: boolean;
  disableSubtasks?: boolean;
}

export function TaskRow({
  task,
  visibleColumns,
  columnWidths,
  boardId,
  timeFormat,
  dateFormat,
  fieldMap,
  customFieldValuesByTask,
  isSelected,
  isSubtaskSelected,
  depth = 0,
  onSelect,
  onOpenDetail,
  onTitleSave,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onDateChange,
  onEstimatedChange,
  onGroupChange,
  onEdit,
  onDelete,
  onSubtaskCreate,
  hideDragHandle,
  hideCheckbox,
  disableSubtasks,
}: TaskRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);

  const subtaskCount = task.subtaskCount ?? task.subtasks?.length ?? 0;
  const { data: fetchedSubtasks } = useTaskSubtasks(expanded && !disableSubtasks ? task.id : null);

  const ctx: RowContext = {
    boardId,
    timeFormat,
    dateFormat,
    onStatusChange,
    onPriorityChange,
    onAssigneeChange,
    onDateChange,
    onEstimatedChange,
    onGroupChange,
    customFieldValuesByTask,
  };

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (hideDragHandle) return;
    const el = rowRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;

    const cleanup1 = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ taskId: task.id, statusId: task.status.id }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });

    const cleanup2 = dropTargetForElements({
      element: el,
      getData: () => ({ taskId: task.id, statusId: task.status.id }),
      onDragEnter: () => setIsDragOver(true),
      onDragLeave: () => setIsDragOver(false),
      onDrop: () => setIsDragOver(false),
    });

    return () => {
      cleanup1();
      cleanup2();
    };
  }, [task.id]);

  function handleCellClick() {
    if (isEditing) return;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onOpenDetail?.(task.id);
    }, 220);
  }

  function handleCellDoubleClick(e: React.MouseEvent) {
    e.preventDefault();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    setIsEditing(true);
  }

  function commitEdit() {
    const trimmed = inputRef.current?.value.trim() ?? '';
    if (trimmed && trimmed !== task.title) {
      onTitleSave?.(task.id, trimmed);
    }
    setIsEditing(false);
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  }

  return (
    <>
      <div
        ref={rowRef}
        data-testid={`tasks-row-${task.id}`}
        style={depth > 0 ? { paddingLeft: `${depth * 24}px` } : undefined}
        className={cn(
          'group/row flex items-center gap-3 border-b border-border/50 transition-colors hover:bg-accent/30',
          isDragging && 'opacity-50',
          isDragOver && 'bg-primary/5 ring-1 ring-inset ring-primary/30',
        )}
      >
        {/* Drag handle */}
        {hideDragHandle ? (
          <div className="w-7 shrink-0" />
        ) : (
          <button
            type="button"
            ref={handleRef}
            data-testid={`tasks-row-${task.id}-drag-handle`}
            className="flex w-7 shrink-0 items-center justify-center p-1.5 cursor-grab touch-none opacity-0 group-hover/row:opacity-100 active:cursor-grabbing"
            aria-label="Drag to reorder"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="size-4 text-muted-foreground" />
          </button>
        )}

        {/* Checkbox */}
        {hideCheckbox ? (
          <div className="w-5 shrink-0" />
        ) : (
          <div
            className="flex w-5 shrink-0 items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              data-testid={`tasks-row-${task.id}-checkbox`}
              checked={isSelected ?? false}
              onCheckedChange={(v) => onSelect?.(task.id, !!v)}
              className={cn(
                'shrink-0 transition-opacity',
                isSelected ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100',
              )}
              aria-label={`Select ${task.title}`}
            />
          </div>
        )}

        {/* Title cell */}
        <TaskTitleCell
          task={task}
          isEditing={isEditing}
          expanded={expanded}
          subtaskCount={subtaskCount}
          disableSubtasks={disableSubtasks}
          depth={depth}
          inputRef={inputRef}
          onClick={handleCellClick}
          onDoubleClick={handleCellDoubleClick}
          onCommitEdit={commitEdit}
          onInputKeyDown={handleInputKeyDown}
          onToggleExpand={() => setExpanded((v) => !v)}
          onAddSubtask={() => {
            setExpanded(true);
            setIsAddingSubtask(true);
          }}
        />

        {/* Dynamic columns — resolved via registry */}
        {visibleColumns
          .filter((c) => c !== 'title')
          .map((col) => {
            const builtIn = BUILT_IN_COLUMN_MAP[col];
            if (builtIn) {
              return (
                <div
                  key={col}
                  style={{ width: getColumnWidthPx(col, builtIn, columnWidths) }}
                  className={cn('hidden shrink-0 p-3', builtIn.breakpoint)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {builtIn.renderCell(task, ctx)}
                </div>
              );
            }

            const field = fieldMap[col];
            if (field) {
              const def = makeCustomFieldColumn(field);
              return (
                <div
                  key={col}
                  style={{ width: getColumnWidthPx(col, def, columnWidths) }}
                  className={cn('hidden shrink-0 p-3', def.breakpoint)}
                >
                  {def.renderCell(task, ctx)}
                </div>
              );
            }

            return null;
          })}

        {/* Overflow menu — only rendered when at least one action is wired up */}
        {(onEdit || onDelete) && (
          <div className="w-8 shrink-0" onClick={(e) => e.stopPropagation()}>
            <OverflowMenu
              data-testid={`tasks-row-${task.id}-overflow-menu`}
              onEdit={onEdit ? () => onEdit(task.id) : undefined}
              onDelete={onDelete ? () => onDelete(task.id) : undefined}
            />
          </div>
        )}
      </div>

      {/* Subtask rows */}
      {expanded && !disableSubtasks && (
        <>
          {fetchedSubtasks?.map((subtask) => (
            <TaskRow
              key={subtask.id}
              task={subtask}
              boardId={boardId}
              visibleColumns={visibleColumns}
              columnWidths={columnWidths}
              timeFormat={timeFormat}
              dateFormat={dateFormat}
              fieldMap={fieldMap}
              customFieldValuesByTask={customFieldValuesByTask}
              depth={depth + 1}
              hideDragHandle
              isSelected={isSubtaskSelected?.(subtask.id) ?? false}
              isSubtaskSelected={isSubtaskSelected}
              onSelect={onSelect}
              onOpenDetail={onOpenDetail}
              onTitleSave={onTitleSave}
              onStatusChange={onStatusChange}
              onPriorityChange={onPriorityChange}
              onAssigneeChange={onAssigneeChange}
              onDateChange={onDateChange}
              onEstimatedChange={onEstimatedChange}
              onGroupChange={onGroupChange}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
          {isAddingSubtask && (
            <div style={{ paddingLeft: `${(depth + 1) * 24}px` }}>
              <InlineTaskRow
                visibleColumns={visibleColumns}
                columnWidths={columnWidths}
                onConfirm={(title) => {
                  onSubtaskCreate?.(task.id, title);
                  setIsAddingSubtask(false);
                }}
                onCancel={() => setIsAddingSubtask(false)}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}
